/**
 * iter86.77.11 — TCR Game UI Standard (user-mandated, 2026-07-19):
 * every major game pane (Leaderboard, Boosts, Standings, Bracket,
 * Schedule) offers a full-screen view that hides the header ribbon,
 * and any table wider than a phone swipes horizontally instead of
 * squashing its columns. Documented in FRD.md §4.
 */
import { useEffect, useState } from "react";
import { Maximize2, X } from "lucide-react";

export function FullscreenShell({ header, onClose, children, testId }) {
    useEffect(() => {
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [onClose]);
    return (
        <div className="fixed inset-0 z-[100] bg-[#0a0a0a] overflow-y-auto" data-testid={testId}>
            <div className="sticky top-0 z-10 bg-[#0a0a0a] border-b-2 border-[#333] px-3 py-2.5 flex items-center gap-2.5">
                <div className="flex-1 min-w-0">{header}</div>
                <button
                    type="button"
                    onClick={onClose}
                    data-testid={`${testId}-close-btn`}
                    className="shrink-0 p-2 border-2 border-[#333] rounded-md text-zinc-400 hover:border-zinc-500 hover:text-white transition-colors"
                    aria-label="Close full-screen view"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>
            <div className="p-2 pb-12">{children}</div>
        </div>
    );
}

export function ExpandButton({ onClick, testId, label = "Full screen" }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className="inline-flex items-center gap-1 px-2 py-1 border border-[#333] rounded-md text-[10px] uppercase tracking-wider font-bold text-zinc-500 hover:text-white hover:border-zinc-500 transition-colors"
            title="Expand to full screen (hides the header ribbon)"
        >
            <Maximize2 className="w-3 h-3" /> {label}
        </button>
    );
}

export function SwipeScroll({ minWidth, children, className = "" }) {
    return (
        <div className={`overflow-x-auto ${className}`}>
            <div style={minWidth ? { minWidth } : undefined}>{children}</div>
        </div>
    );
}

export default function ExpandablePane({ title, testId, children }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            {!open && (
                <div>
                    <div className="flex justify-end mb-1.5">
                        <ExpandButton onClick={() => setOpen(true)} testId={`${testId}-expand-btn`} />
                    </div>
                    {children}
                </div>
            )}
            {open && (
                <FullscreenShell
                    testId={`${testId}-fullscreen`}
                    onClose={() => setOpen(false)}
                    header={
                        <p className="font-display font-extrabold uppercase tracking-tight text-sm truncate">
                            {title}
                        </p>
                    }
                >
                    {children}
                </FullscreenShell>
            )}
        </>
    );
}
